// @flow
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useForm } from 'react-hook-form';
import { HeaderContainer, HeaderContent } from 'components/shared/PageHeader/PageHeader';
import { Title, SmallTitle } from 'components/shared/CustomizeGoalcardTitle/Title';
import ErrorLabel from 'components/shared/ErrorLabel/ErrorLabel';
import purchaseActions from 'actions/purchase';
import bgImage from 'assets/images/background.png';
import { sendAnalyticsEvent } from 'utils/analyticsWrapper';
import { nameWithPossesive } from 'utils/string';
import ReactTooltip from 'react-tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import './stripeInputStyles.css';
import {
  PageContainer,
  DesktopTitle,
  MobileTitle,
  PaymentContainer,
  GoalcardSummaryContainer,
  CardPaymentContainer,
  CardNameInput,
  Button,
  GoalcardSummaryBoxContainer,
  GoalcardSummaryBoxHeading,
  GoalcardSummaryHeadingTitle,
  GoalcardSummaryBoxContent,
  GoalcardSummaryBoxImage,
  SummaryDetail,
  LabelDetail,
  LabelDetailValue,
  TotalDetail,
  TotalDetailLabel,
  ButtonContainer,
  TOSLabel,
  TOSLink,
  Divider,
} from './Styles';

type PurchaseProps = {
  goalcardPayload: { creditCardToken: string },
  purchaseGoalcard(payload: any): void,
  isProcessing: Boolean,
  responseCode: Integer
};

const PurchaseGoalcard = (props: PurchaseProps) => {
  const landingsURL = process.env.REACT_APP_LANDINGS_URL;
  const { t } = useTranslation();
  const history = useHistory();
  const { handleSubmit, register, formState, errors } = useForm();
  const [cardError, setError]: [any, ((any => any) | any) => void] = useState();
  const [cardHolderName, setCardHolderName]: [any, ((any => any) | any) => void] = useState();
  const [hasError, setHasError]: [any, ((any => any) | any) => void] = useState(false);

  const [pageState, updatePageState]: [any, ((any => any) | any) => void] = useState({
    isSubmitted: false
  });

  const {
    goalcardPayload,
    purchaseGoalcard,
    isProcessing,
    responseCode,
  } = props;

  const {
    amount,
    destinationFirstname,
    destinationLastname,
    investorName,
    goalPrettyName,
    goalcardImage,
    executionDate,
  } = goalcardPayload || {};

  const options = {
    style: {
      base: {
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        fontSmoothing: 'antialiased',
        fontSize: '14px',
        '::placeholder': { color: '#aab7c4' },
      },
      invalid: { color: 'red', iconColor: 'red' },
    },
  };

  const stripe = useStripe();
  const elements = useElements();

  const onFormSubmit = async () => {

    console.log(pageState.isSubmitted);

    // Stripe.js has not loaded yet.
    // Make sure to disable form submission until Stripe.js has loaded.
    if (!stripe || !elements || pageState.isSubmitted) {
      return;
    }

    updatePageState({ ...pageState, isSubmitted: true });

    setHasError(false);

    // Get a reference to a mounted CardElement. Elements knows how
    // to find your CardElement because there can only ever be one of
    // each type of element.
    const cardElement = elements.getElement(CardElement);

    const data = { name: cardHolderName };

    // // Use your card Element with other Stripe.js APIs
    const { token } = await stripe.createToken(cardElement, data);

    goalcardPayload.creditCardToken = token.id;

    purchaseGoalcard(goalcardPayload)
      .then(() => {
        sendAnalyticsEvent('Purchase Goalcard', 'purchase_goalcard_success');
        history.push('/success');
      })
      .catch(() => {
        sendAnalyticsEvent('Purchase Goalcard', 'purchase_goalcard_error');
        setHasError(true);
      });
  };

  const onInputChange = (e) => setCardHolderName(e.target.value);

  const getTotalWithFee = (goalcardAmount) => {
    if (goalcardAmount) {
      const parsedAmount = parseFloat(goalcardAmount);

      const totalWithFee = (parsedAmount + 1 + 0.30) / 0.971;

      return Number(parseFloat(totalWithFee));
    }

    return Number(0);
  };

  const getFee = (goalcardAmount) => {
    if (goalcardAmount) {
      const fee = getTotalWithFee(goalcardAmount) - parseFloat(goalcardAmount);

      return Number(parseFloat(fee));
    }

    return Number(0);
  };

  const onCardElementChange = (e) => {
    if (e.error && e.error.message) {
      setError(e.error.message);
    } else {
      setError(null);
    }
  };

  const loadResponseError = () => {
    let errorText = t('purchase_generalerror_title');

    if (executionDate) {
      errorText = t('purchase_schedule_unhandled_error_title');
    }

    if (responseCode) {
      if (responseCode === 456) {
        errorText = t('purchase_nsferror_title');
      }
      if (responseCode === 457) {
        errorText = t('purchase_declinederror_title');
      }
      if (responseCode === 458) {
        errorText = t('purchase_invalidinvestor_title');
      }
      if (responseCode === 459) {
        errorText = t('purchase_restricted_account_error_title', { receiver: nameWithPossesive(destinationFirstname) });
      }
    }

    if (hasError) {
      return <ErrorLabel visible={hasError} text={errorText} />;
    }
    return null;
  };

  const renderTitle = () => (
    <Title>
      {t('purchase_payment_title')}
      <FontAwesomeIcon data-tip data-for="orderTooltip" icon={faQuestionCircle} style={{ marginLeft: '5px' }} />
      <ReactTooltip className="tooltip-fixed-size" id="orderTooltip" place="top" effect="solid">
        {t('purchase_goalsetter_order_tooltip_title')}
      </ReactTooltip>
    </Title>
  );

  return (
    <form onSubmit={handleSubmit(onFormSubmit)}>
      <HeaderContainer backgroundImage={bgImage}>
        <HeaderContent>
          {t('purchase_page_title')}
        </HeaderContent>
      </HeaderContainer>
      <PageContainer mobileView={isMobile}>
        <PaymentContainer>
          <DesktopTitle>
            {renderTitle()}
          </DesktopTitle>
          <CardPaymentContainer>
            <SmallTitle>{t('purchase_card_fullname_title')}</SmallTitle>
            <CardNameInput data-testid="inputCardHolderName" name="cardHolderName" ref={register({ required: true })} onChange={onInputChange} />
            {errors.cardHolderName?.type === 'required' && <ErrorLabel data-testid="lblErrorCardHolderName" text={t('purchase_summary_carderror_title')} />}
            <Divider />
            <SmallTitle>{t('purchase_card_details_title')}</SmallTitle>
            <CardElement options={options} onChange={onCardElementChange} />
            {cardError ? <ErrorLabel data-testid="lblCardErrors" text={cardError} /> : null}
            {loadResponseError()}
          </CardPaymentContainer>
          <ButtonContainer>
            <Button
              text={t('purchase_button_title')}
              loading={!stripe || isProcessing}
              data-testid="btnSubmit"
              type="submit"
            />
            <TOSLabel>
              {t('purchase_tos_title')}
              <TOSLink href={`${landingsURL}/terms-of-use`} target="_blank" rel="noreferrer">{t('purchase_tos_link')}</TOSLink>
            </TOSLabel>
          </ButtonContainer>
        </PaymentContainer>
        <GoalcardSummaryContainer>
          <MobileTitle>
            {renderTitle()}
          </MobileTitle>
          <GoalcardSummaryBoxHeading>
            <GoalcardSummaryHeadingTitle>{t('purchase_summary_title')}</GoalcardSummaryHeadingTitle>
          </GoalcardSummaryBoxHeading>
          <GoalcardSummaryBoxContainer>
            <GoalcardSummaryBoxContent>
              <GoalcardSummaryBoxImage src={goalcardImage} />
              <SummaryDetail>
                <LabelDetail>{t('goalcard_to')}</LabelDetail>
                <LabelDetailValue>{`${destinationFirstname} ${destinationLastname}`}</LabelDetailValue>
              </SummaryDetail>
              <SummaryDetail>
                <LabelDetail>{t('goalcard_from')}</LabelDetail>
                <LabelDetailValue>{investorName}</LabelDetailValue>
              </SummaryDetail>
              <SummaryDetail>
                <LabelDetail>{t('goalcard_for')}</LabelDetail>
                <LabelDetailValue>{goalPrettyName || t('customize_forpreset_title')}</LabelDetailValue>
              </SummaryDetail>
              <SummaryDetail>
                <LabelDetailValue>{t('purchase_summary_total_title')}</LabelDetailValue>
                <LabelDetailValue alignment="right">{`$${amount}`}</LabelDetailValue>
              </SummaryDetail>
              <SummaryDetail>
                <LabelDetailValue width="100%">
                  {t('purchase_summary_fee_title')}
                  <FontAwesomeIcon data-tip data-for="feeTooltip" icon={faQuestionCircle} style={{ marginLeft: '5px', fontSize: '14px' }} />
                  <ReactTooltip className="tooltip-fixed-size" id="feeTooltip" place="right" effect="solid">
                    {t('purchase_goalsetter_fee_title')}
                  </ReactTooltip>
                </LabelDetailValue>
                <LabelDetailValue data-testid="fee" alignment="right">{`$${getFee(amount).toFixed(2)}`}</LabelDetailValue>
              </SummaryDetail>
              <TotalDetail>
                <TotalDetailLabel>{t('total_title')}</TotalDetailLabel>
                <TotalDetailLabel data-testid="total" alignment="right">{`$${getTotalWithFee(amount).toFixed(2)}`}</TotalDetailLabel>
              </TotalDetail>
            </GoalcardSummaryBoxContent>
          </GoalcardSummaryBoxContainer>
        </GoalcardSummaryContainer>
      </PageContainer>
    </form>
  );
};

// Map Props
const mapStateToProps = (state) => ({
  isProcessing: state.purchase.isProcessing,
  goalcardPayload: state.purchase.goalcardPayload,
  responseCode: state.purchase.responseCode
});

// Map Dispatch
const mapDispatchToProps = (dispatch) => ({
  purchaseGoalcard: (payload: any) => dispatch(
    purchaseActions.purchaseGoalcard(payload),
  ),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(PurchaseGoalcard);
